import {
  type APIUser,
  roleColorHex,
  selectDisplayName,
  selectMemberColor,
  selectPresence,
} from '@alternativamais/chat-core';
import { useEffect, useState } from 'react';

import { useEstado } from './contexto';

/** Relógio que anda sozinho (tempo relativo, "digitando" que vence). */
export function useAgora(intervaloMs = 30_000): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), intervaloMs);
    return () => window.clearInterval(id);
  }, [intervaloMs]);
  return agora;
}

/** Nome que aparece: apelido no servidor, nome de exibição ou usuário. */
export function useNome(
  userId: string,
  guildId?: string | null,
  reserva?: APIUser,
): string {
  return useEstado((estado) =>
    estado.users[userId] || !reserva
      ? selectDisplayName(estado, userId, guildId)
      : (guildId && estado.members[guildId]?.[userId]?.nick) ||
        reserva.global_name ||
        reserva.username,
  );
}

/** Cor do cargo mais alto com cor (nula: cor do texto). */
export function useCorDoNome(
  userId: string,
  guildId?: string | null,
): string | null {
  const cor = useEstado((estado) =>
    guildId ? selectMemberColor(estado, guildId, userId) : null,
  );
  return cor ? roleColorHex(cor) : null;
}

export function useStatus(userId: string) {
  return useEstado((estado) => selectPresence(estado, userId));
}

/** Eu (a conta logada). */
export function useEu(): APIUser {
  const eu = useEstado((estado) => estado.me);
  if (!eu) throw new Error('Sem READY.');
  return eu;
}

/** Modo desenvolvedor (U11): só com ele os menus mostram "Copiar ID", como no Discord. */
export function useModoDesenvolvedor(): boolean {
  return useEstado((estado) => !!estado.userSettings?.developer_mode);
}

/** Uma preferência da conta (`user_settings`), com o padrão do Discord quando ainda não chegou. */
export function useAjusteDaConta<
  K extends
    | 'message_display_compact'
    | 'inline_attachment_media'
    | 'render_embeds'
    | 'gif_auto_play'
    | 'animate_emoji'
    | 'render_reactions'
    | 'convert_emoticons',
>(chave: K): boolean {
  const padrao = chave !== 'message_display_compact';
  return useEstado((estado) => estado.userSettings?.[chave] ?? padrao);
}
